/**
 * retryFetch.ts
 *
 * A lightweight retry wrapper for apiFetch calls that may transiently fail
 * with a 503 (upstream IdP timeout) or a network / fetch error.
 *
 * Design constraints:
 *  - Only retries TRANSIENT status codes (503) and network errors.
 *  - Never retries auth errors (401/403) — those are real and must surface
 *    immediately so the caller can handle them correctly.
 *  - Uses a simple fixed-delay backoff between attempts.
 *  - apiFetch is passed as a parameter so this helper is easy to unit-test
 *    without wiring up the full fetch stack.
 */

/** Status codes that are safe to retry (transient infrastructure failures). */
const RETRYABLE_STATUS_PATTERN = /^503$/;

/**
 * Returns true for error messages thrown by apiFetch on a 503 response.
 * apiFetch throws `new Error(detail)` where `detail` is the parsed body text
 * (e.g. "context service unavailable", "Service Unavailable") or the HTTP
 * statusText. We match case-insensitively on "unavailable" / "503".
 */
function isRetryableError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = err.message.toLowerCase();
  // Network / fetch failures (no response at all)
  if (msg === 'failed to fetch' || msg === 'network error' || msg === 'load failed') {
    return true;
  }
  // 503 — body text from apiFetch or explicit status code in message
  if (RETRYABLE_STATUS_PATTERN.test(msg)) return true;
  if (msg.includes('unavailable') || msg.includes('service unavailable')) return true;
  return false;
}

/** Millisecond delay between attempt N-1 and N (0-based attempt index). */
const BACKOFF_MS = [400, 1000] as const;

export interface RetryOptions {
  /** Number of ADDITIONAL attempts after the first (default: 2). */
  maxRetries?: number;
  /**
   * Override backoff schedule in ms.  Length determines effective maxRetries
   * if shorter than maxRetries.
   */
  backoffMs?: readonly number[];
  /**
   * Optional callback fired before each retry (not the first attempt).
   * Receives the 1-based retry index (1 = first retry) and the error that
   * triggered the retry.
   */
  onRetry?: (attempt: number, err: unknown) => void;
}

/**
 * Calls `fn()` and retries on transient 503 / network errors with backoff.
 *
 * Auth errors (401/403) are NOT retried and propagate immediately.
 *
 * @param fn       Async function to call (typically an apiFetch call).
 * @param opts     Retry configuration.
 * @returns        Resolved value of `fn()`.
 * @throws         The last error if all attempts fail.
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  opts: RetryOptions = {},
): Promise<T> {
  const maxRetries = opts.maxRetries ?? 2;
  const backoffMs = opts.backoffMs ?? BACKOFF_MS;

  let lastErr: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt > 0) {
      const delayMs = backoffMs[attempt - 1] ?? backoffMs[backoffMs.length - 1];
      await new Promise<void>(resolve => setTimeout(resolve, delayMs));
      opts.onRetry?.(attempt, lastErr);
    }
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      // Non-retryable errors bubble up immediately (401, 403, 4xx, etc.)
      if (!isRetryableError(err)) {
        throw err;
      }
      // If this was the last attempt, fall through and re-throw below.
      if (attempt === maxRetries) break;
    }
  }
  throw lastErr;
}
